<template>
  <div class="overview-page">
    <header class="view-header">
      <h1>Trading Dashboard</h1>
      <p class="subtitle">Live pricing and timing for GLDM, SLV, and CPER.</p>
    </header>

    <div class="overview-intel-bar">
      <div class="overview-intel-card">
        <span class="overview-intel-label">Best Trade</span>
        <strong>Silver +5-9%</strong>
        <span class="overview-intel-copy">May 7 → May 10 · Neptune Station + Moon Aquarius</span>
      </div>
      <div class="overview-intel-card">
        <span class="overview-intel-label">Strongest Signal</span>
        <strong>Silver 4.7</strong>
        <span class="overview-intel-copy">May 7-10 · Neptune Station + Moon Aquarius</span>
      </div>
      <div class="overview-intel-card">
        <span class="overview-intel-label">Copper Window</span>
        <strong>Apr 17-23</strong>
        <span class="overview-intel-copy">Score 63 HIGH · Mars Aries + Venus Taurus</span>
      </div>
    </div>

    <div class="price-grid">
      <div
        v-for="c in commodityCards"
        :key="c.id"
        :class="['price-card', c.id]"
        @click="$router.push(c.link)"
      >
        <div class="card-header">
          <span :class="['card-icon', c.bgClass]">
            <img :src="c.icon" :alt="c.name">
          </span>
          <span class="name">{{ c.ticker }}</span>
        </div>
        <div class="card-body">
          <span class="price">{{ formatPrice(marketStore.prices[c.id]?.price) }}</span>
          <span :class="['change', changeDir(c.id)]">
            {{ formatChange(marketStore.prices[c.id]) }}
          </span>
        </div>
        <div class="card-footer">
          <span class="score">Score: <strong>{{ c.score }}</strong></span>
          <span :class="['action', c.actionClass]">{{ c.action }}</span>
        </div>
      </div>
    </div>

    <div class="overview-research">
      <div class="recommendation-panel">
        <div class="section-header">
          <div>
            <h2>Current Recommendations</h2>
            <p class="section-subtitle">Short live read for each tracked asset.</p>
          </div>
          <span class="overview-section-tag">Summary</span>
        </div>
        <div class="recommendation-grid">
          <div v-for="rec in recommendations" :key="rec.name" class="recommendation-row">
            <div class="recommendation-row-copy">
              <strong>{{ rec.name }}</strong>
              <span>{{ rec.detail }}</span>
            </div>
            <div class="recommendation-row-meta">
              <span class="recommendation-score">{{ rec.score }}</span>
              <span :class="['action', rec.actionClass]">{{ rec.action }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <section class="section timeline-section">
      <div class="section-header">
        <div>
          <h2>Buy / Sell Timeline</h2>
          <p class="section-subtitle">April 16 — May 31, 2026 · All 11 trades across Gold, Silver, Copper · Win rates from backtest validation</p>
        </div>
        <span class="overview-section-tag">11 Trades</span>
      </div>
      <div class="timeline-table-wrap">
        <table class="timeline-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Metal</th>
              <th>BUY</th>
              <th>SELL</th>
              <th>Score</th>
              <th>Expected</th>
              <th>Win Rate</th>
              <th>Key Signal</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(t, i) in trades" :key="i">
              <td class="row-num">{{ i + 1 }}</td>
              <td><span :class="['metal-badge', t.color]">{{ t.metal }}</span></td>
              <td class="date-cell">{{ t.buy }}</td>
              <td class="date-cell">{{ t.sell }}</td>
              <td class="score-cell">{{ t.score }}</td>
              <td class="expected-cell">{{ t.expected }}</td>
              <td class="winrate-cell">
                <span :class="['winrate-badge', winRateClass(t.winRate)]">{{ t.winRate }}</span>
              </td>
              <td class="signal-cell">{{ t.signal }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="section signals-section">
      <div class="section-header">
        <h2>High-Confidence Signals</h2>
        <span class="overview-section-tag">Signals</span>
      </div>
      <TimingWindowTable :windows="timingWindows" title="Active Timing Windows" />
    </section>
  </div>
</template>

<script setup>
import { onMounted } from 'vue';
import { useMarketStore } from '../stores/market';
import TimingWindowTable from '../components/TimingWindowTable.vue';

const marketStore = useMarketStore();

const commodityCards = [
  {
    id: 'gold',
    name: 'Gold',
    ticker: 'GLDM',
    icon: 'https://dummyimage.com/36x36/b68a2e/fff&text=G',
    bgClass: 'gold-bg',
    link: '/gold',
    score: 51,
    action: 'BUY',
    actionClass: 'buy',
  },
  {
    id: 'silver',
    name: 'Silver',
    ticker: 'SLV',
    icon: 'https://dummyimage.com/36x36/5f6b7a/fff&text=S',
    bgClass: 'silver-bg',
    link: '/silver',
    score: 10.3,
    action: 'BUY',
    actionClass: 'buy',
  },
  {
    id: 'copper',
    name: 'Copper',
    ticker: 'CPER',
    icon: 'https://dummyimage.com/36x36/f97316/fff&text=C',
    bgClass: 'copper-bg',
    link: '/copper',
    score: 63,
    action: 'BUY',
    actionClass: 'buy',
  },
];

const recommendations = [
  { name: 'Gold (GLDM)', detail: 'WAIT until Aug 2-7. J-V Sextile EXACT, 82.5% WR, +1.9%. No validated entry until then.', score: '71', action: 'WAIT', actionClass: 'hold' },
  { name: 'Silver (SLV)', detail: '5 trade windows. Best: May 7-10 (Neptune Station, score 4.7, +5-9%). Cumulative +20-37%.', score: '4.7', action: 'BUY', actionClass: 'buy' },
  { name: 'Copper (CPER)', detail: 'BUY Apr 17 → Apr 23. Mars Aries + Venus Taurus, score 63 HIGH. Only 2 windows, done by May 18.', score: '63', action: 'BUY', actionClass: 'buy' },
];

const timingWindows = [
  { type: 'BUY', startDate: '2026-04-17', endDate: '2026-04-18', signal: 'Silver: Mercury Ingress + Waxing Moon', expected: '+3-6%', winRate: '83.9%' },
  { type: 'BUY', startDate: '2026-04-17', endDate: '2026-04-23', signal: 'Copper: Mars Aries + Venus Taurus + 4 Conjunctions', expected: '+2.8%', winRate: '79.4%' },
  { type: 'BUY', startDate: '2026-04-24', endDate: '2026-04-27', signal: 'Silver: Venus Ingress + Waxing Moon', expected: '+3-5%', winRate: '76.6%' },
  // REMOVED: Gold May 4 Post-New Moon (50% WR, Sh -0.10 — loser)
  // REMOVED: Gold May 17 Moon Scorpio (50% WR, Sh 0.01 — coin flip)
  // REMOVED: Gold May 25 V-J Conjunction (36% WR, Sh -0.45 — disaster)
  { type: 'AVOID', startDate: '2026-05-29', endDate: '2026-06-11', signal: 'Gold: Venus-Pluto Opposition · Bearish', expected: '0 to -1%', winRate: '48%' },
  { type: 'BUY', startDate: '2026-05-07', endDate: '2026-05-10', signal: 'Silver: Neptune Station + Moon Aquarius', expected: '+5-9%', winRate: '78.7%' },
  { type: 'BUY', startDate: '2026-05-08', endDate: '2026-05-16', signal: 'Copper: Venus-Mars sextile + Moon Aries', expected: '+2.3%', winRate: '65%' },
  { type: 'BUY', startDate: '2026-05-17', endDate: '2026-05-21', signal: 'Silver: Mercury Ingress + Venus Ingress', expected: '+5-10%', winRate: '83.9%' },
  { type: 'BUY', startDate: '2026-05-23', endDate: '2026-05-25', signal: 'Silver: Double Mutable + Waxing Moon', expected: '+4-7%', winRate: '77.7%' },
  { type: 'BUY', startDate: '2026-08-02', endDate: '2026-08-07', signal: 'Gold: Jupiter-Venus Sextile EXACT · 82.5% WR', expected: '+1.9%', winRate: '82.5%' },
];

const trades = [
  { metal: 'Copper', buy: 'Apr 17', sell: 'Apr 23', score: 63, expected: '+2.8%', winRate: '79.4%', signal: 'Mars Aries + Venus Taurus + 4 Conjunctions', color: 'copper' },
  { metal: 'Silver', buy: 'Apr 17', sell: 'Apr 18', score: 3.2, expected: '+3-6%', winRate: '83.9%', signal: 'Mercury Ingress + Waxing Moon', color: 'silver' },
  { metal: 'Gold', buy: 'Aug 2', sell: 'Aug 12', score: 71, expected: '+1.9%', winRate: '82.5%', signal: 'Jupiter-Venus Sextile EXACT · 10d hold', color: 'gold' },
  { metal: 'Silver', buy: 'Apr 24', sell: 'Apr 27', score: 3.4, expected: '+3-5%', winRate: '76.6%', signal: 'Venus Ingress + Waxing Moon', color: 'silver' },
  { metal: 'Silver', buy: 'May 7', sell: 'May 10', score: 4.7, expected: '+5-9%', winRate: '78.7%', signal: 'Neptune Station + Moon Aquarius', color: 'silver' },
  { metal: 'Copper', buy: 'May 8', sell: 'May 16', score: 40, expected: '+2.3%', winRate: '65%', signal: 'Venus-Mars sextile + Moon Aries', color: 'copper' },
  { metal: 'Silver', buy: 'May 17', sell: 'May 21', score: 4.2, expected: '+5-10%', winRate: '83.9%', signal: 'Mercury Ingress + Venus Ingress', color: 'silver' },
  { metal: 'Silver', buy: 'May 23', sell: 'May 25', score: 3.9, expected: '+4-7%', winRate: '77.7%', signal: 'Double Mutable + Waxing Moon', color: 'silver' },
];

const formatPrice = (price) => {
  if (!price) return '--';
  if (price >= 1000) return `$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  return `$${price.toFixed(2)}`;
};

const winRateClass = (rate) => {
  const num = parseFloat(rate);
  if (isNaN(num)) return '';
  if (num >= 78) return 'high';
  if (num >= 68) return 'good';
  return 'moderate';
};

const changeDir = (id) => {
  const change = marketStore.prices[id]?.change;
  if (!change) return '';
  return change >= 0 ? 'up' : 'down';
};

const formatChange = (data) => {
  if (!data || !data.change) return '--';
  const sign = data.change >= 0 ? '+' : '';
  return `${sign}${data.change.toFixed(2)} (${sign}${data.changePercent?.toFixed(2)}%)`;
};

onMounted(async () => {
  await marketStore.fetchPrices();
});
</script>

<style scoped>
.overview-page {
  max-width: 1160px;
  margin: 0 auto;
  animation: fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

.view-header {
  margin-bottom: 1.35rem;
}

.view-header h1 {
  font-size: 2.05rem;
  font-weight: 600;
  margin-bottom: 0.28rem;
  letter-spacing: -0.035em;
}

.view-header .subtitle {
  color: var(--text-secondary);
  font-size: 0.92rem;
  max-width: 68ch;
}

/* Intel Bar */
.overview-intel-bar {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem;
  margin-bottom: 1rem;
}

.overview-intel-card {
  background: rgba(252, 250, 246, 0.96);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  padding: 0.9rem 1rem;
  display: grid;
  gap: 0.2rem;
  box-shadow: var(--panel-shadow);
  backdrop-filter: blur(6px);
}

.overview-intel-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-dim);
}

.overview-intel-card strong {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
}

.overview-intel-copy {
  color: var(--text-secondary);
  font-size: 0.8rem;
  line-height: 1.45;
}

/* Price Grid */
.price-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
  margin-bottom: 1.5rem;
}

.price-card {
  background: rgba(252, 250, 246, 0.82);
  border-radius: 16px;
  padding: 1.1rem 1.2rem;
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
  box-shadow: var(--panel-shadow);
}

.price-card:hover {
  transform: translateY(-3px);
  border-color: var(--text-dim);
  box-shadow: var(--glass-shadow);
}

.price-card.gold:hover { border-color: var(--gold); box-shadow: 0 12px 24px var(--gold-dim); }
.price-card.silver:hover { border-color: var(--silver); box-shadow: 0 12px 24px var(--silver-dim); }
.price-card.copper:hover { border-color: #f97316; box-shadow: 0 12px 24px rgba(249, 115, 22, 0.18); }

.card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
}

.card-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.card-icon img {
  width: 18px;
  height: 18px;
  object-fit: contain;
  display: block;
}

.gold-bg { background: var(--gold-dim); }
.silver-bg { background: var(--silver-dim); }
.copper-bg { background: rgba(249, 115, 22, 0.10); }

.card-header .name {
  font-size: 0.82rem;
  color: var(--text-secondary);
}

.card-body {
  margin-bottom: 0.75rem;
}

.card-body .price {
  display: block;
  font-size: 1.6rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
}

.card-body .change {
  font-size: 0.84rem;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
}

.card-body .change.up { color: var(--bullish); }
.card-body .change.down { color: var(--danger); }

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
}

.card-footer .score {
  color: var(--text-dim);
  font-family: var(--font-mono);
}

.card-footer .action {
  padding: 0.24rem 0.58rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
}

.action.buy { background: rgba(16, 185, 129, 0.15); color: var(--bullish); }
.action.hold { background: rgba(156, 163, 175, 0.15); color: var(--text-secondary); }
.action.avoid { background: rgba(239, 68, 68, 0.15); color: var(--danger); }
.action.caution { background: rgba(245, 158, 11, 0.15); color: var(--warning); }

/* Recommendations */
.overview-research {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
  margin-bottom: 1.5rem;
}

.recommendation-panel {
  background: rgba(252, 250, 246, 0.98);
  border-radius: 16px;
  border: 1px solid var(--border-color);
  padding: 1.25rem 1.25rem 0.95rem;
  backdrop-filter: blur(6px);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.15rem;
}

.section-header h2 {
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: 0.15rem;
}

.section-subtitle {
  color: var(--text-secondary);
  font-size: 0.82rem;
}

.overview-section-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.9rem;
  background: var(--accent-dim);
  color: var(--accent);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-radius: 20px;
}

.recommendation-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
}

.recommendation-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.9rem;
  padding: 0.8rem 0.9rem;
  border-radius: 10px;
  background: var(--bg-hover);
}

.recommendation-row-copy {
  display: grid;
  gap: 0.14rem;
  min-width: 0;
}

.recommendation-row-copy strong {
  font-size: 0.92rem;
  line-height: 1.25;
}

.recommendation-row-copy span {
  color: var(--text-secondary);
  font-size: 0.82rem;
  line-height: 1.35;
}

.recommendation-row-meta {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  flex-shrink: 0;
}

.recommendation-row-meta .recommendation-score {
  font-size: 0.74rem;
  color: var(--text-secondary);
}

/* Signals Section */
.section {
  background: rgba(252, 250, 246, 0.98);
  border-radius: 16px;
  border: 1px solid var(--border-color);
  padding: 1.25rem;
  box-shadow: var(--panel-shadow);
  backdrop-filter: blur(6px);
  margin-bottom: 1.5rem;
}

/* Buy/Sell Timeline Table */
.timeline-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 10px;
  border: 1px solid var(--border-color);
}

.timeline-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;
}

.timeline-table thead {
  background: var(--bg-hover);
}

.timeline-table th {
  padding: 0.65rem 0.75rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-dim);
  white-space: nowrap;
}

.timeline-table td {
  padding: 0.6rem 0.75rem;
  border-top: 1px solid var(--border-color);
  vertical-align: middle;
}

.timeline-table tbody tr:hover {
  background: var(--bg-hover);
}

.row-num {
  color: var(--text-dim);
  font-family: var(--font-mono);
  font-size: 0.78rem;
  width: 2rem;
}

.metal-badge {
  display: inline-block;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.74rem;
  letter-spacing: 0.03em;
}

.metal-badge.gold { background: rgba(182, 138, 46, 0.15); color: var(--gold); }
.metal-badge.silver { background: var(--silver-dim); color: var(--silver); }
.metal-badge.copper { background: rgba(249, 115, 22, 0.12); color: #f97316; }

.date-cell {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.score-cell {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: var(--text-primary);
}

.expected-cell {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: var(--bullish);
  white-space: nowrap;
}

.winrate-cell {
  white-space: nowrap;
}

.winrate-badge {
  display: inline-block;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  font-family: var(--font-mono);
}

.winrate-badge.high {
  background: rgba(34, 197, 94, 0.18);
  color: var(--success);
}

.winrate-badge.good {
  background: rgba(34, 197, 94, 0.10);
  color: #16a34a;
}

.winrate-badge.moderate {
  background: rgba(234, 179, 8, 0.15);
  color: var(--warning);
}

.signal-cell {
  color: var(--text-secondary);
  font-size: 0.8rem;
  max-width: 28ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Responsive */
@media (max-width: 1100px) {
  .overview-intel-bar,
  .price-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .recommendation-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .overview-intel-bar,
  .price-grid {
    grid-template-columns: 1fr;
  }
  .view-header h1 {
    font-size: 1.4rem;
  }
  .view-header .subtitle {
    font-size: 0.82rem;
  }
  .overview-intel-bar {
    gap: 0.65rem;
  }
  .overview-intel-card {
    padding: 0.75rem 0.85rem;
  }
  .overview-intel-card strong {
    font-size: 0.88rem;
  }
  .overview-intel-copy {
    font-size: 0.75rem;
  }
  .price-card {
    padding: 0.9rem 1rem;
  }
  .card-body .price {
    font-size: 1.4rem;
  }
  .recommendation-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.55rem;
  }
  .recommendation-row-meta {
    width: 100%;
    justify-content: space-between;
  }
  .section-header {
    flex-direction: column;
    gap: 0.5rem;
  }
  .section-header h2 {
    font-size: 0.95rem;
  }
  .overview-section-tag {
    align-self: flex-start;
  }
  .signals-section {
    border-radius: 12px;
    padding: 1rem;
  }
  .signals-section .section-header {
    margin-bottom: 0.85rem;
  }
  .signals-section .timing-window-table {
    border-radius: 10px;
  }
  .timeline-section {
    padding: 1rem;
    border-radius: 12px;
  }
  .timeline-table {
    font-size: 0.78rem;
  }
  .timeline-table th,
  .timeline-table td {
    padding: 0.5rem 0.55rem;
  }
  .signal-cell {
    max-width: 18ch;
  }
}
</style>
